// Pure, product-agnostic parent-map resolver (arch doc §4.3a).
// A parent-map maps a route *pattern* to { parent, labelKey, resolveLabel?, fallbackKey? }.
// Patterns may contain a single :param per segment (e.g. '/listings/:listingId').
// The core NEVER hardcodes a product route; all route data lives in *.tla config.

const HOME = '/';

function segments(pathname) {
  return String(pathname || '').split('/').filter(Boolean);
}

// Does a concrete pathname match a route pattern that may contain :params?
function patternMatches(pattern, pathname) {
  const ps = segments(pattern);
  const xs = segments(pathname);
  if (ps.length !== xs.length) return false;
  for (let i = 0; i < ps.length; i++) {
    if (ps[i].startsWith(':')) continue; // a :param matches any single segment
    if (ps[i] !== xs[i]) return false;
  }
  return true;
}

// Find the map entry whose key matches pathname: exact key first, then :param patterns.
function findEntry(map, pathname) {
  if (map[pathname]) return { pattern: pathname, entry: map[pathname] };
  for (const pattern of Object.keys(map)) {
    if (pattern.includes(':') && patternMatches(pattern, pathname)) {
      return { pattern, entry: map[pattern] };
    }
  }
  return null;
}

// Resolve the parent-map entry for a pathname.
// Direct/param match → the entry. No match → strip the last segment and retry (matcher
// fallback), treating the unknown deep route as a child of its nearest known ancestor.
// Nothing resolvable → a safe fallback entry with a null parent (caller routes to dashboard).
export function resolveParentEntry(map, pathname) {
  const found = findEntry(map, pathname);
  if (found) return { ...found.entry, pattern: found.pattern };

  const segs = segments(pathname);
  for (let cut = segs.length - 1; cut >= 1; cut--) {
    const candidate = '/' + segs.slice(0, cut).join('/');
    const f = findEntry(map, candidate);
    if (f) {
      return { parent: candidate, labelKey: f.entry.labelKey, pattern: candidate, synthesized: true };
    }
  }
  return { parent: null, labelKey: 'experience.dashboard.home', synthesized: true };
}

// Build the breadcrumb trail by walking parent links from current → root, then reversing.
// Returns [{ path, labelKey, fallbackKey?, current }]. The last item is the current page.
export function resolveBreadcrumbTrail(map, pathname) {
  const trail = [];
  const seen = new Set();
  let current = pathname === '' ? HOME : pathname;
  while (current && !seen.has(current)) {
    seen.add(current);
    const entry = resolveParentEntry(map, current);
    trail.push({ path: current, labelKey: entry.labelKey, fallbackKey: entry.fallbackKey, current: false });
    current = entry.parent;
  }
  trail.reverse();
  if (trail.length) trail[trail.length - 1].current = true;
  return trail;
}
